"use client";

import React, { useMemo } from "react";
import {
  VenuePageUrlBase,
  computeNavItems,
  getBasePath,
} from "./navigation/utils";
import {
  useVenuePage,
  useVenuePageCustomLinks,
  useVenuePageExternalLinks,
  useVenuePageFAQs,
  useVenuePageNavGroups,
  useVenuePageNavItems,
  useVenuePageTheme,
} from "../../hooks";

import Image from "next/image";
import Link from "next/link";
import { RadialGradientBackground } from "@punchup/ui-common/components/common";
import { VenuePageNavThemed } from "./navigation";
import { useMediaQuery } from "@punchup/utils/hooks";
import { usePathname } from "next/navigation";

interface VenuePageTopSectionProps {
  venueId: string;
  venueSlug: string;
  venueName: string;
  imgUrl: string | null;
  urlBase: VenuePageUrlBase;
}

export default function VenuePageTopSectionThemed({
  venueId,
  venueSlug,
  venueName,
  imgUrl,
  urlBase,
}: VenuePageTopSectionProps) {
  const {
    logoImageBackgroundColor,
    topSectionBackgroundColor: backgroundColor,
    topSectionGradientColor: gradientColor,
  } = useVenuePageTheme(venueSlug);
  const { venuePage } = useVenuePage({ slug: venueSlug });
  const { customLinks } = useVenuePageCustomLinks(venueId);
  const { externalLinks } = useVenuePageExternalLinks(venueId);
  const { navItems } = useVenuePageNavItems(venueId);
  const { navGroups } = useVenuePageNavGroups(venueId);
  const { faqs } = useVenuePageFAQs(venueId);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const hasFAQs = faqs?.length > 0;
  const hasMenu = !!venuePage?.food_menu || !!venuePage?.drinks_menu;

  // Render an sr-only H1 only on the homepage. The hero section is image-only
  // by design, so the H1 is hidden visually but exposed to assistive tech and
  // search engines. Inner pages (shows-tonight, free-shows, about, …) keep
  // their own visible page heading.
  const pathname = usePathname();
  const basePath = getBasePath(urlBase, venueSlug);
  const isHomePage =
    pathname === basePath ||
    pathname === `${basePath}/` ||
    (basePath === "" && pathname === "/");

  // Compute nav items (memoized to avoid recomputation on every render)
  const computedNavItems = useMemo(() => {
    if (!venuePage) return [];
    return computeNavItems({
      venuePage,
      customLinks,
      externalLinks,
      navItems,
      navGroups,
      hasFAQs,
      hasMenu,
      urlBase,
    });
  }, [
    venuePage,
    customLinks,
    externalLinks,
    navItems,
    navGroups,
    hasFAQs,
    hasMenu,
    urlBase,
  ]);

  if (!venuePage) {
    return null;
  }

  return (
    <RadialGradientBackground
      colors={{
        ...(gradientColor !== null
          ? { start: gradientColor }
          : { start: backgroundColor }), // using the same color as the background makes a "no gradient" effect
        end: backgroundColor,
      }}
      positions={{
        start: "0%",
        end: isDesktop ? "30%" : "70%",
      }}
      centerPosition="50% 0%"
      className="flex flex-col pt-2.5 pb-4 md:py-6 md:pt-4 lg:px-20"
    >
      {imgUrl && (
        <div className="flex flex-row justify-center items-center px-4">
          <Link
            href={`${getBasePath(urlBase, venueSlug)}/`}
            className="flex flex-row items-center justify-start object-contain h-full w-auto max-w-[250px]"
          >
            <Image
              src={imgUrl}
              alt={`${venueName} logo`}
              height={250}
              width={250}
              sizes="250px"
              style={{
                width: "auto",
                backgroundColor: logoImageBackgroundColor,
              }}
              className="object-contain h-[100px] md:h-[133px] p-2.5 md:p-3 rounded-md"
              priority
            />
          </Link>
        </div>
      )}
      {isHomePage && (
        <h1 className="sr-only">
          {venuePage.city_state
            ? `${venueName} — comedy club in ${venuePage.city_state}`
            : `${venueName} — comedy club`}
        </h1>
      )}
      <div className="pt-4 md:pt-6">
        <VenuePageNavThemed
          urlBase={urlBase}
          venueSlug={venueSlug}
          navItems={computedNavItems}
          customLinks={customLinks}
          externalLinks={externalLinks}
        />
      </div>
    </RadialGradientBackground>
  );
}
